// src/components/AddEditStaffModal/components/BasicInfoTab.js
import React, { useState, useEffect, useRef } from 'react';
import { Checkbox, Divider, FormControlLabel, Grid, IconButton, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import InsertInvitationIcon from '@material-ui/icons/InsertInvitation';
import { CustomTextField } from '../../../Textfield';
import { CustomDatePicker } from '../../../DateTimePicker';
import { LabeledSwitch, StyledMenuItem } from './FormUI';
import { GeofenceAutoApprovalIcon } from '../../../../assets/svg';
import { TrackingEnabledIcon } from '../../../../assets/svg';
import { SelfieAttendanceIcon } from '../../../../assets/svg';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { isBlueDartOrg } from '../../../../utils/appHelper';

const GENDER_OPTIONS = [
    { label: 'Male', value: 'M' },
    { label: 'Female', value: 'F' },
];

// Keep in sync with BluedartConstants.BD_EMPLOYEE_MODES on the backend
const EMPLOYEE_MODE_OPTIONS = [
    { label: 'MPC', value: 'MPC' },
    { label: 'PDA', value: 'PDA' },
    { label: 'IPDA', value: 'IPDA' },
    { label: 'FESTIVE', value: 'FESTIVE' },
];



export default function BasicInfoTab({
    handlers,
    vehicleTypesList,
    vendorList,
    formik,
    sccList = [],
    rollCodeList = [],
    educationalQualificationOptions = [],
    shirtSizeOptions = [],
    trouserSizeOptions = [],
    shoeSizeOptions = [],
    isEdit,
}) {
    const staffTypes = useSelector((state) => state?.staff?.types);
    const leadSources = useSelector((state) => state?.staff?.leadSources);
    const phoneEditable = useSelector(
        (state) => state?.user?.config?.phoneEditable,
    );
    const isBlueDart = !!isBlueDartOrg?.();

    const selectedStaffType = staffTypes?.find(
        (t) => String(t.id) === String(formik.values.staffTypeId),
    );
    const isInHouseStaff = !!selectedStaffType?.name
        ?.toLowerCase()
        ?.includes('in house');

    const selectedVehicle = vehicleTypesList?.find(
        (v) => String(v.id) === String(formik.values.vehicleTypeId),
    );
    const isDlRequired = isBlueDart && !isInHouseStaff && !!selectedVehicle?.dlMandatory;

    // The section is "open" if it was validated or any individual doc is verified.
    // `documentValidation` is a computed "all valid" flag, NOT the main checkbox
    // state, so it must not drive whether the sub-checkboxes are shown.
    const anyDocChecked =
        !!formik.values.documentValidation ||
        !!formik.values.aadhaarVerified ||
        !!formik.values.panVerified ||
        !!formik.values.dlVerified;
    const [mainDocChecked, setMainDocChecked] = useState(anyDocChecked);
    const [aadhaarChecked, setAadhaarChecked] = useState(!!formik.values.aadhaarVerified);
    const [panChecked, setPanChecked] = useState(!!formik.values.panVerified);
    const [dlChecked, setDlChecked] = useState(!!formik.values.dlVerified);

    useEffect(() => {
        setAadhaarChecked(!!formik.values.aadhaarVerified);
        setPanChecked(!!formik.values.panVerified);
        setDlChecked(!!formik.values.dlVerified);
        // Keep the section open when data indicates docs exist, but never
        // auto-collapse here — collapsing is only driven by the main checkbox
        // handler. This prevents the sub-checkboxes from disappearing when
        // ticking Aadhaar/PAN flips the computed `documentValidation` to false.
        if (
            formik.values.documentValidation ||
            formik.values.aadhaarVerified ||
            formik.values.panVerified ||
            formik.values.dlVerified
        ) {
            setMainDocChecked(true);
        }
    }, [formik.values.documentValidation, formik.values.aadhaarVerified, formik.values.panVerified, formik.values.dlVerified]);

    const vehicleTypeIdInitialized = useRef(false);
    useEffect(() => {
        if (!vehicleTypeIdInitialized.current) {
            vehicleTypeIdInitialized.current = true;
            return;
        }
        if (isBlueDart && !isEdit) {
            setDlChecked(false);
            formik.setFieldValue('dlVerified', false);
        }
    }, [formik.values.vehicleTypeId]);

    // Disable phone field in edit mode when masked (flag from BE or X-pattern on stored number).
    const isMaskedPhone =
        !!formik.values.maskDetails ||
        /^X+\d+$/i.test(formik.values.contactNumber || '');
    const isPhoneDisabled = (isEdit && !phoneEditable) || (isEdit && isMaskedPhone);

    const syncDocumentValidation = (main, aadhaar, pan, dl) => {
        const allValid = main && aadhaar && pan && (!isDlRequired || dl);
        formik.setFieldValue('documentValidation', allValid);
        formik.setFieldTouched('documentValidation', true, false);
    };

    const handleMainDocChange = (e) => {
        const checked = e.target.checked;
        setMainDocChecked(checked);
        if (!checked) {
            setAadhaarChecked(false);
            setPanChecked(false);
            setDlChecked(false);
            formik.setFieldValue('documentValidation', false);
            formik.setFieldValue('aadhaarVerified', false);
            formik.setFieldValue('panVerified', false);
            formik.setFieldValue('dlVerified', false);
            formik.setFieldTouched('documentValidation', true, false);
        } else {
            syncDocumentValidation(true, aadhaarChecked, panChecked, dlChecked);
        }
    };

    const handleAadhaarChange = (e) => {
        setAadhaarChecked(e.target.checked);
        formik.setFieldValue('aadhaarVerified', e.target.checked);
        syncDocumentValidation(mainDocChecked, e.target.checked, panChecked, dlChecked);
    };

    const handlePanChange = (e) => {
        setPanChecked(e.target.checked);
        formik.setFieldValue('panVerified', e.target.checked);
        syncDocumentValidation(mainDocChecked, aadhaarChecked, e.target.checked, dlChecked);
    };

    const handleDlChange = (e) => {
        setDlChecked(e.target.checked);
        formik.setFieldValue('dlVerified', e.target.checked);
        syncDocumentValidation(mainDocChecked, aadhaarChecked, panChecked, e.target.checked);
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={6}>
                <CustomTextField
                    label="Staff Name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="name"
                    isRequired={true}
                    fullWidth
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                />
            </Grid>

            <Grid item xs={3}>
                <CustomTextField
                    label="Staff Type"
                    select
                    fullWidth={true}
                    value={formik.values?.staffTypeId ?? ''}
                    name="staffTypeId"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={
                        isBlueDart && isEdit && !!formik.values.staffTypeId
                    }
                    defaultValue=""
                    style={{
                        marginBottom: '16px',
                        //width: '50%',
                        marginRight: 5,
                    }}
                    error={
                        formik.touched.staffTypeId &&
                        Boolean(formik.errors.staffTypeId)
                    }
                    helperText={
                        formik.touched.staffTypeId && formik.errors.staffTypeId
                    }
                    isRequired
                >
                    {staffTypes?.length ? (
                        staffTypes.map((hub, index) => (
                            <StyledMenuItem key={index} value={hub.id}>
                                {hub.name}
                            </StyledMenuItem>
                        ))
                    ) : (
                        <StyledMenuItem value={null}>
                            No Data Found
                        </StyledMenuItem>
                    )}
                </CustomTextField>
            </Grid>
            <Grid item xs={3}>
                <CustomTextField
                    label="Phone Number"
                    value={formik.values.contactNumber}
                    onChange={(e) => {
                        e.target.value = e.target.value.replace(/\D/g, '');
                        formik.handleChange(e);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === ' ') e.preventDefault();
                    }}
                    onBlur={formik.handleBlur}
                    name="contactNumber"
                    isRequired={true}
                    inputProps={{ maxLength: 11, inputMode: 'numeric' }}
                    fullWidth
                    disabled={isPhoneDisabled}
                    error={
                        formik.touched.contactNumber &&
                        Boolean(formik.errors.contactNumber)
                    }
                    helperText={
                        formik.touched.contactNumber &&
                        formik.errors.contactNumber
                    }
                />
            </Grid>

            {!isBlueDart && (
                <Grid item xs={6}>
                    <CustomDatePicker
                        label="Date of Join"
                        value={formik.values.joiningDateEpochMillis}
                        name="joiningDateEpochMillis"
                        onChange={(e) => {
                            formik.handleChange({
                                target: {
                                    name: 'joiningDateEpochMillis',
                                    value: e,
                                },
                            });
                        }}
                        onBlur={formik.handleBlur}
                        showInput
                        InputProps={{
                            endAdornment: (
                                <IconButton>
                                    <InsertInvitationIcon />
                                </IconButton>
                            ),
                        }}
                        fullWidth
                        isRequired={true}
                        error={
                            formik.touched.joiningDateEpochMillis &&
                            Boolean(formik.errors.joiningDateEpochMillis)
                        }
                        helperText={
                            formik.touched.joiningDateEpochMillis &&
                            formik.errors.joiningDateEpochMillis
                        }
                    />
                </Grid>
            )}

            <Grid item xs={6}>
                <CustomDatePicker
                    label="Date of Birth"
                    value={
                        formik.values.dateOfBirthEpochMillis != null &&
                        moment(
                            formik.values.dateOfBirthEpochMillis,
                        ).isValid() &&
                        moment().diff(
                            moment(formik.values.dateOfBirthEpochMillis),
                            'years',
                        ) >= 18
                            ? formik.values.dateOfBirthEpochMillis
                            : null
                    }
                    name="dateOfBirthEpochMillis"
                    onChange={(e) => {
                        formik.handleChange({
                            target: {
                                name: 'dateOfBirthEpochMillis',
                                value: e,
                            },
                        });
                    }}
                    onBlur={formik.handleBlur}
                    showInput={true}
                    allowAge18={false}
                    allowFutureDates={false}
                    InputProps={{
                        endAdornment: (
                            <IconButton>
                                <InsertInvitationIcon />
                            </IconButton>
                        ),
                    }}
                    fullWidth
                    isRequired={true}
                    error={
                        formik.touched.dateOfBirthEpochMillis &&
                        Boolean(formik.errors.dateOfBirthEpochMillis)
                    }
                    helperText={
                        formik.touched.dateOfBirthEpochMillis &&
                        formik.errors.dateOfBirthEpochMillis
                    }
                />
            </Grid>

            {/* Hide MOT for In House Staff; show for Delivery Boy (both BlueDart and non-BlueDart) */}
            {!isInHouseStaff && (
                <Grid item xs={6}>
                    <CustomTextField
                        label={isBlueDart ? 'MOT' : 'Vehicle Type'}
                        value={formik.values.vehicleTypeId}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        name="vehicleTypeId"
                        select
                        fullWidth
                        isRequired={!isInHouseStaff}
                        disabled={
                            isBlueDart &&
                            isEdit &&
                            !!formik.values.vehicleTypeId
                        }
                        error={
                            formik.touched.vehicleTypeId &&
                            Boolean(formik.errors.vehicleTypeId)
                        }
                        helperText={
                            formik.touched.vehicleTypeId &&
                            formik.errors.vehicleTypeId
                        }
                    >
                        {vehicleTypesList?.length ? (
                            vehicleTypesList?.map((vehicle, index) => (
                                <StyledMenuItem key={index} value={vehicle?.id}>
                                    {vehicle?.value}
                                </StyledMenuItem>
                            ))
                        ) : (
                            <StyledMenuItem value={null}>
                                No Data Found
                            </StyledMenuItem>
                        )}
                    </CustomTextField>
                </Grid>
            )}

            {!isBlueDart && !!formik.values.vehicleTypeId && (
                <Grid item xs={6}>
                    <CustomTextField
                        label="Vehicle Number"
                        value={formik.values.vehicleNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        name="vehicleNumber"
                        isRequired={false}
                        fullWidth
                        error={
                            formik.touched.vehicleNumber &&
                            Boolean(formik.errors.vehicleNumber)
                        }
                        helperText={
                            formik.touched.vehicleNumber &&
                            formik.errors.vehicleNumber
                        }
                    />
                </Grid>
            )}

            {/* Blue Dart specific fields */}
            {isBlueDart && (
                <>
                    <Grid item xs={6}>
                        <CustomTextField
                            label="Gender"
                            select
                            fullWidth
                            value={formik.values.gender ?? ''}
                            name="gender"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isRequired
                            error={
                                formik.touched.gender &&
                                Boolean(formik.errors.gender)
                            }
                            helperText={
                                formik.touched.gender && formik.errors.gender
                            }
                        >
                            {GENDER_OPTIONS.map((opt) => (
                                <StyledMenuItem
                                    key={opt.value}
                                    value={opt.value}
                                >
                                    {opt.label}
                                </StyledMenuItem>
                            ))}
                        </CustomTextField>
                    </Grid>

                    <Grid item xs={6}>
                        <Autocomplete
                            options={sccList}
                            noOptionsText="No service centres found"
                            getOptionLabel={(scc) =>
                                `${scc?.sccCode ?? ''} - ${scc?.locationName ?? ''}`
                            }
                            value={
                                sccList.find(
                                    (scc) =>
                                        String(scc.id) ===
                                        String(formik.values.sccCode),
                                ) || null
                            }
                            onChange={(_, newValue) =>
                                formik.setFieldValue(
                                    'sccCode',
                                    newValue?.id ?? '',
                                )
                            }
                            onBlur={() => formik.setFieldTouched('sccCode', true)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={<>Service Centre Code (SCC) <span style={{ color: 'red' }}>*</span></>}
                                    variant="outlined"
                                    fullWidth
                                    error={
                                        formik.touched.sccCode &&
                                        Boolean(formik.errors.sccCode)
                                    }
                                    helperText={
                                        formik.touched.sccCode &&
                                        formik.errors.sccCode
                                    }
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <CustomTextField
                            label="Employee Mode"
                            select
                            fullWidth
                            value={formik.values.employeeMode ?? ''}
                            name="employeeMode"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isRequired
                            error={
                                formik.touched.employeeMode &&
                                Boolean(formik.errors.employeeMode)
                            }
                            helperText={
                                formik.touched.employeeMode &&
                                formik.errors.employeeMode
                            }
                        >
                            {EMPLOYEE_MODE_OPTIONS.map((opt) => (
                                <StyledMenuItem
                                    key={opt.value}
                                    value={opt.value}
                                >
                                    {opt.label}
                                </StyledMenuItem>
                            ))}
                        </CustomTextField>
                    </Grid>

                    {!!formik.values.staffTypeId && (
                        <Grid item xs={6}>
                            <CustomTextField
                                label="Roll Code"
                                select
                                fullWidth
                                value={formik.values.rollCode ?? ''}
                                name="rollCode"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isRequired
                                error={
                                    formik.touched.rollCode &&
                                    Boolean(formik.errors.rollCode)
                                }
                                helperText={
                                    formik.touched.rollCode &&
                                    formik.errors.rollCode
                                }
                            >
                                {rollCodeList?.length ? (
                                    rollCodeList.map((rc) => (
                                        <StyledMenuItem
                                            key={rc.id}
                                            value={rc.id}
                                        >
                                            {rc.role ?? rc.code ?? rc.id}
                                        </StyledMenuItem>
                                    ))
                                ) : (
                                    <StyledMenuItem value="">
                                        No Roll Codes Found
                                    </StyledMenuItem>
                                )}
                            </CustomTextField>
                        </Grid>
                    )}

                    <Grid item xs={6}>
                        <CustomTextField
                            label="Educational Qualification"
                            select
                            fullWidth
                            value={formik.values.educationalQualification ?? ''}
                            name="educationalQualification"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isRequired
                            error={
                                formik.touched.educationalQualification &&
                                Boolean(formik.errors.educationalQualification)
                            }
                            helperText={
                                formik.touched.educationalQualification &&
                                formik.errors.educationalQualification
                            }
                        >
                            {educationalQualificationOptions.map((opt) => (
                                <StyledMenuItem
                                    key={opt.value}
                                    value={opt.value}
                                >
                                    {opt.label}
                                </StyledMenuItem>
                            ))}
                        </CustomTextField>
                    </Grid>

                    {/* New BD fields */}
                    <Grid item xs={6}>
                        <CustomTextField
                            label="Email ID"
                            fullWidth
                            value={formik.values.emailId ?? ''}
                            name="emailId"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isRequired
                            error={
                                formik.touched.emailId &&
                                Boolean(formik.errors.emailId)
                            }
                            helperText={
                                formik.touched.emailId && formik.errors.emailId
                            }
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <CustomDatePicker
                            label="Uniform Issue Date"
                            value={formik.values.uniformIssueDate ?? null}
                            name="uniformIssueDate"
                            onChange={(e) => {
                                formik.handleChange({
                                    target: {
                                        name: 'uniformIssueDate',
                                        value: e,
                                    },
                                });
                            }}
                            onBlur={formik.handleBlur}
                            showInput
                            InputProps={{
                                endAdornment: (
                                    <IconButton>
                                        <InsertInvitationIcon />
                                    </IconButton>
                                ),
                            }}
                            fullWidth
                            error={
                                formik.touched.uniformIssueDate &&
                                Boolean(formik.errors.uniformIssueDate)
                            }
                            helperText={
                                formik.touched.uniformIssueDate &&
                                formik.errors.uniformIssueDate
                            }
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <CustomTextField
                            label="Shirt Size"
                            select
                            fullWidth
                            value={formik.values.shirtSize ?? ''}
                            name="shirtSize"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isRequired
                            error={
                                formik.touched.shirtSize &&
                                Boolean(formik.errors.shirtSize)
                            }
                            helperText={
                                formik.touched.shirtSize && formik.errors.shirtSize
                            }
                        >
                            {shirtSizeOptions?.length ? (
                                shirtSizeOptions.map((opt) => (
                                    <StyledMenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </StyledMenuItem>
                                ))
                            ) : (
                                <StyledMenuItem value="">No Data Found</StyledMenuItem>
                            )}
                        </CustomTextField>
                    </Grid>

                    <Grid item xs={6}>
                        <CustomTextField
                            label="Trouser Size (inches)"
                            select
                            fullWidth
                            value={formik.values.trouserSize ?? ''}
                            name="trouserSize"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isRequired
                            error={
                                formik.touched.trouserSize &&
                                Boolean(formik.errors.trouserSize)
                            }
                            helperText={
                                formik.touched.trouserSize && formik.errors.trouserSize
                            }
                        >
                            {trouserSizeOptions?.length ? (
                                trouserSizeOptions.map((opt) => (
                                    <StyledMenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </StyledMenuItem>
                                ))
                            ) : (
                                <StyledMenuItem value="">No Data Found</StyledMenuItem>
                            )}
                        </CustomTextField>
                    </Grid>

                    <Grid item xs={6}>
                        <CustomTextField
                            label="Shoe Size (UK/India)"
                            select
                            fullWidth
                            value={formik.values.shoeSize ?? ''}
                            name="shoeSize"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isRequired
                            error={
                                formik.touched.shoeSize &&
                                Boolean(formik.errors.shoeSize)
                            }
                            helperText={
                                formik.touched.shoeSize && formik.errors.shoeSize
                            }
                        >
                            {shoeSizeOptions?.length ? (
                                shoeSizeOptions.map((opt) => (
                                    <StyledMenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </StyledMenuItem>
                                ))
                            ) : (
                                <StyledMenuItem value="">No Data Found</StyledMenuItem>
                            )}
                        </CustomTextField>
                    </Grid>

                    <Grid item xs={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!formik.values.interviewTaken}
                                    name="interviewTaken"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    color="primary"
                                    disabled={isEdit}
                                />
                            }
                            label={<span>Interview Taken <span style={{ color: 'red' }}>*</span></span>}
                        />
                        {formik.touched.interviewTaken &&
                            formik.errors.interviewTaken && (
                                <Typography variant="caption" color="error" display="block">
                                    {formik.errors.interviewTaken}
                                </Typography>
                            )}
                    </Grid>

                    <Grid item xs={12}>
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={mainDocChecked}
                                        onChange={handleMainDocChange}
                                        color="primary"
                                        disabled={isEdit}
                                    />
                                }
                                label={<span>Document Validated <span style={{ color: 'red' }}>*</span></span>}
                            />
                            {mainDocChecked && (
                                <>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={aadhaarChecked}
                                                onChange={handleAadhaarChange}
                                                color="primary"
                                                disabled={isEdit}
                                            />
                                        }
                                        label={<span>Aadhaar <span style={{ color: 'red' }}>*</span></span>}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={panChecked}
                                                onChange={handlePanChange}
                                                color="primary"
                                                disabled={isEdit}
                                            />
                                        }
                                        label={<span>PAN <span style={{ color: 'red' }}>*</span></span>}
                                    />
                                    {isDlRequired && (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={dlChecked}
                                                    onChange={handleDlChange}
                                                    color="primary"
                                                    disabled={isEdit}
                                                />
                                            }
                                            label={<span>DL <span style={{ color: 'red' }}>*</span></span>}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                        {formik.touched.documentValidation &&
                            formik.errors.documentValidation && (
                                <Typography variant="caption" color="error" display="block">
                                    {formik.errors.documentValidation}
                                </Typography>
                            )}
                    </Grid>
                </>
            )}

            {!isBlueDart && (
                <Grid item xs={12}>
                    <Typography
                        style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            marginBottom: '18px',
                        }}
                        variant="body1"
                    >
                        Attendance Setup
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <LabeledSwitch
                                title="Enable Selfie"
                                icon={
                                    <SelfieAttendanceIcon
                                        width={24}
                                        height={24}
                                        style={{
                                            marginRight: '8px',
                                        }}
                                    />
                                }
                                checked={formik.values.selfieEnabled}
                                name="selfieEnabled"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                            <LabeledSwitch
                                title="Enable Track & Trace"
                                icon={
                                    <TrackingEnabledIcon
                                        width={24}
                                        height={24}
                                        style={{
                                            marginRight: '8px',
                                        }}
                                    />
                                }
                                checked={formik.values.trackAndTraceEnabled}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                name="trackAndTraceEnabled"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <LabeledSwitch
                                title="Enable Geofence Auto Approval"
                                icon={
                                    <GeofenceAutoApprovalIcon
                                        width={24}
                                        height={24}
                                        style={{
                                            marginRight: '8px',
                                        }}
                                    />
                                }
                                checked={formik.values.geofenceAutoApproval}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                name="geofenceAutoApproval"
                            />
                        </Grid>
                    </Grid>
                    <Divider />
                </Grid>
            )}

            {!isBlueDart && (
                <Grid item xs={12}>
                    <Typography
                        style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            marginBottom: '18px',
                        }}
                        variant="body1"
                    >
                        Source of Delivery Boy
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <CustomTextField
                                label={
                                    formik?.values?.userCreationSource
                                        ? 'Source'
                                        : 'Select rider source'
                                }
                                select
                                fullWidth={true}
                                value={formik?.values?.userCreationSource ?? ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                name="userCreationSource"
                                defaultValue=""
                                style={{
                                    marginBottom: '16px',
                                    width: '100%',
                                    marginRight: 5,
                                }}
                                isRequired={true}
                                error={
                                    formik.touched.userCreationSource &&
                                    Boolean(formik.errors.userCreationSource)
                                }
                                helperText={
                                    formik.touched.userCreationSource &&
                                    formik.errors.userCreationSource
                                }
                            >
                                {leadSources?.staticValues?.length &&
                                    leadSources.staticValues.map(
                                        (source, index) => (
                                            <StyledMenuItem
                                                key={index}
                                                value={source.name}
                                            >
                                                {source.name}
                                            </StyledMenuItem>
                                        ),
                                    )}
                            </CustomTextField>
                        </Grid>
                        {formik?.values.userCreationSource &&
                            formik?.values.userCreationSource === 'Vendor' && (
                                <Grid item xs={6}>
                                    <CustomTextField
                                        label={
                                            formik?.values?.vendorId
                                                ? 'Vendor'
                                                : 'Select Vendor'
                                        }
                                        select
                                        fullWidth={true}
                                        value={formik?.values?.vendorId ?? ''}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        name="vendorId"
                                        defaultValue=""
                                        style={{
                                            marginBottom: '16px',
                                            width: '100%',
                                            marginRight: 5,
                                        }}
                                        isRequired={true}
                                    >
                                        {vendorList?.length ? (
                                            vendorList?.map((vendor, index) => (
                                                <StyledMenuItem
                                                    key={index}
                                                    value={vendor?.vendorId}
                                                >
                                                    {vendor?.name}
                                                </StyledMenuItem>
                                            ))
                                        ) : (
                                            <StyledMenuItem value={null}>
                                                No Data Found
                                            </StyledMenuItem>
                                        )}
                                    </CustomTextField>
                                </Grid>
                            )}
                        <Grid item xs={6}>
                            <CustomTextField
                                fullWidth={true}
                                label={
                                    formik?.values?.sourceRemarks
                                        ? 'Remarks'
                                        : 'Enter remarks if any'
                                }
                                value={formik?.values?.sourceRemarks}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                name="sourceRemarks"
                                style={{ marginBottom: '16px' }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
}
